import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * BackendText component - handles display of (pre-translated) backend text with fallbacks
 * 
 * @param {Object} props - Component props
 * @param {string} props.text - The text to display (expected to be translated by parent)
 * @param {string} props.i18nKey - Optional i18n key as fallback
 * @param {string} props.fallback - Optional fallback text if both text and i18nKey are unavailable
 * @param {string} props.className - Additional CSS classes
 * @param {React.ElementType} props.as - The element type to render (default: span)
 */
const BackendText = ({ 
  text, 
  i18nKey,
  fallback = '',
  className = '', 
  as: Component = 'span', 
  ...rest 
}) => {
  const { t } = useTranslation();
  
  // Determine what text to display, with fallback logic
  let displayText = text;
  
  if (!displayText && i18nKey) {
    displayText = t(i18nKey, { defaultValue: fallback });
  } else if (!displayText) {
    displayText = fallback;
  }
  
  if (process.env.NODE_ENV === 'development') {
    if (!text && i18nKey && displayText !== fallback) {
      console.debug(`[BackendText] Using i18n fallback for key: ${i18nKey}`);
    }
  }
  
  if (!text && !i18nKey && !fallback) return null;
  
  return (
    <Component className={`backend-text ${className}`} {...rest}>
      {displayText}
    </Component>
  );
};

export default BackendText;

/**
 * BackendHeading component - A convenience component for headings from backend
 * Uses BackendText internally with a dynamic heading level
 */
export const BackendHeading = ({
  text,
  i18nKey,
  fallback = '',
  level = 2,
  className = '',
  ...props
}) => {
  const Component = `h${level}`;
  
  return (
    <BackendText
      text={text}
      i18nKey={i18nKey}
      fallback={fallback}
      as={Component}
      className={`backend-heading ${className}`}
      {...props}
    />
  );
};
